import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen } from "lucide-react";
import { SeoHead } from "@/lib/seo";
import { getGlossary } from "@/lib/seo-glossary";
import { Breadcrumb, breadcrumbJsonLd } from "@/components/Breadcrumb";
import { useLang } from "@/lib/lang-context";

const COPY = {
  it: {
    metaTitle: "Glossario immobiliare del Salento — Termini e definizioni | Deal Rating",
    metaDesc:
      "Dizionario dei termini immobiliari del Salento: trullo, pajara, vincolo paesaggistico, OMI, Xylella, fascia costiera, IMU, agibilità e altri 20+ termini chiave.",
    breadcrumb: { home: "Home", glossary: "Glossario" },
    eyebrow: "Glossario",
    h1: "Glossario immobiliare Salento",
    intro:
      "Tutte le parole che ti servono per capire un annuncio o una visura: dai trulli ai vincoli del PPTR, dall'OMI alla Xylella. Definizioni essenziali in ordine alfabetico.",
    setName: "Glossario immobiliare Salento",
    sortLocale: "it",
  },
  en: {
    metaTitle: "Salento real-estate glossary — Terms and definitions | Deal Rating",
    metaDesc:
      "Dictionary of Salento real-estate terminology: trullo, pajara, landscape constraints, OMI, Xylella, 300m coastal strip, IMU, agibilità and 20+ other key terms.",
    breadcrumb: { home: "Home", glossary: "Glossary" },
    eyebrow: "Glossary",
    h1: "Salento real-estate glossary",
    intro:
      "Every term you need to read an Italian listing or cadastral record: from trulli to PPTR constraints, from OMI data to Xylella. Essential definitions in alphabetical order.",
    setName: "Salento real-estate glossary",
    sortLocale: "en",
  },
};

/**
 * Real-estate Salento glossary (/glossario). Static list of 20 key terms
 * with concise definitions. Emits a DefinedTermSet JSON-LD so Google can
 * understand it as a vocabulary resource. Bilingual: lang from context.
 */
const Glossary = () => {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.it;
  const entries = getGlossary(lang);
  const sorted = [...entries].sort((a, b) =>
    a.term.localeCompare(b.term, c.sortLocale)
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    name: c.setName,
    inLanguage: lang === "en" ? "en" : "it",
    inDefinedTermSet: sorted.map((t) => ({
      "@type": "DefinedTerm",
      name: t.term,
      description: t.def,
    })),
  };
  const breadcrumbItems = [
    { label: c.breadcrumb.home, to: "/" },
    { label: c.breadcrumb.glossary },
  ];

  return (
    <main data-testid="glossary-page" className="bg-tufo">
      <SeoHead
        title={c.metaTitle}
        description={c.metaDesc}
        path="/glossario"
        jsonLd={[jsonLd, breadcrumbJsonLd(breadcrumbItems)]}
      />

      <section className="max-w-3xl mx-auto px-5 md:px-10 pt-10 md:pt-16 pb-10 md:pb-12">
        <Breadcrumb items={breadcrumbItems} />
        <div className="label-eyebrow mt-6 mb-3 flex items-center gap-2">
          <BookOpen size={12} strokeWidth={1.75} /> {c.eyebrow}
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#282C2A] leading-[1.05] tracking-tight">
          {c.h1}
        </h1>
        <p className="mt-5 md:mt-6 text-sm md:text-base text-[#282C2A] leading-relaxed max-w-2xl">
          {c.intro}
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-5 md:px-10 pb-20">
        <dl className="divide-y divide-[#E6E4DD] border-y border-[#E6E4DD]">
          {sorted.map((entry) => (
            <div
              key={entry.term}
              data-testid={`glossary-term-${entry.term.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="py-5 md:py-6 grid md:grid-cols-[200px_1fr] gap-2 md:gap-6"
            >
              <dt className="font-serif-display text-lg md:text-xl text-[#282C2A] leading-tight">
                {entry.term}
              </dt>
              <dd className="text-sm text-[#282C2A] leading-relaxed">
                {entry.def}
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </main>
  );
};

export default Glossary;
